/**
 * Supplier onboarding — statuses, steps and the rules that order them.
 *
 * This file is the single source of truth the shell, the navigator and every step
 * screen read from. The status values mirror the backend enum exactly; changing a
 * string here without changing it there silently breaks the gate that decides which
 * RFQs a supplier may see, so keep the two in sync.
 */

/**
 * Verification pipeline.
 *
 * Registered → ContactVerified → PendingVerification → Verified | MoreInfoRequested | Rejected
 *
 * VerificationExpired and Suspended are terminal states reached later in a supplier's
 * life, not part of the sign-up walk. They are listed because the UI has to render them.
 */
export const ONBOARDING_STATUS = {
  REGISTERED: 'Registered',
  CONTACT_VERIFIED: 'ContactVerified',
  PENDING_VERIFICATION: 'PendingVerification',
  VERIFIED: 'Verified',
  MORE_INFO_REQUESTED: 'MoreInfoRequested',
  REJECTED: 'Rejected',
  VERIFICATION_EXPIRED: 'VerificationExpired',
  SUSPENDED: 'Suspended'
}

/** Maps a status onto a `vp-chip--*` modifier so every screen badges it the same way. */
export const ONBOARDING_STATUS_VARIANT = {
  [ONBOARDING_STATUS.REGISTERED]: 'muted',
  [ONBOARDING_STATUS.CONTACT_VERIFIED]: 'info',
  [ONBOARDING_STATUS.PENDING_VERIFICATION]: 'warning',
  [ONBOARDING_STATUS.VERIFIED]: 'success',
  [ONBOARDING_STATUS.MORE_INFO_REQUESTED]: 'warning',
  [ONBOARDING_STATUS.REJECTED]: 'danger',
  [ONBOARDING_STATUS.VERIFICATION_EXPIRED]: 'danger',
  [ONBOARDING_STATUS.SUSPENDED]: 'danger'
}

/**
 * Statuses in which the supplier may still change their answers.
 *
 * Once submitted the draft is what an admin is reading, so it locks. MoreInfoRequested
 * deliberately reopens it — that status exists precisely to ask for another edit.
 */
export const EDITABLE_STATUSES = [
  ONBOARDING_STATUS.REGISTERED,
  ONBOARDING_STATUS.CONTACT_VERIFIED,
  ONBOARDING_STATUS.MORE_INFO_REQUESTED
]

/** Step identifiers. These are also the child route paths under /onboarding. */
export const ONBOARDING_STEP = {
  COMPANY: 'company',
  ADDRESSES: 'addresses',
  DOCUMENTS: 'documents',
  CERTIFICATES: 'certificates',
  SECURITY: 'security',
  REVIEW: 'review'
}

/**
 * The draft sections the server stores separately, each behind its own endpoint.
 *
 * The later steps write through endpoints of their own (documents, certificates, 2FA)
 * rather than through the draft.
 */
export const ONBOARDING_SECTION = {
  COMPANY_IDENTITY: 'companyIdentity',
  ADDRESSES: 'addresses'
}

/**
 * Step registry, in the order the navigator lists them.
 *
 * `sections` names the draft sections a step owns, so the autosave can route a payload
 * to the right endpoint without each step view knowing about the service. Steps whose
 * data lives elsewhere carry an empty list and save through their own service.
 *
 * `optional` marks a step that does not block submission. It is still listed, still
 * evaluated and still shown -- a supplier should be able to see what they are skipping.
 *
 * The UI numbers them 1–6 rather than by the spec's step numbers, which have gaps (the
 * agreements are accepted at sign-up, certificates were added later): showing a
 * supplier a gap in the count invites them to hunt for the missing screens.
 */
export const ONBOARDING_STEPS = [
  {
    key: ONBOARDING_STEP.COMPANY,
    icon: 'ph-buildings',
    sections: [ONBOARDING_SECTION.COMPANY_IDENTITY]
  },
  {
    key: ONBOARDING_STEP.ADDRESSES,
    icon: 'ph-map-pin-line',
    sections: [ONBOARDING_SECTION.ADDRESSES]
  },
  {
    key: ONBOARDING_STEP.DOCUMENTS,
    icon: 'ph-files',
    sections: []
  },
  {
    // Optional: many legitimate suppliers hold no certificates. Stored as the profile's
    // tenant certificates, so anything added here shows on the profile and vice versa.
    // Mirrors OptionalSteps in SupplierOnboardingConsts.
    key: ONBOARDING_STEP.CERTIFICATES,
    icon: 'ph-certificate',
    sections: [],
    optional: true
  },
  {
    // Optional: two-factor protects the supplier's own account rather than anything a
    // reviewer checks, so it is offered rather than demanded. Mirrors OptionalSteps in
    // SupplierOnboardingConsts -- the server is what actually allows the submit.
    key: ONBOARDING_STEP.SECURITY,
    icon: 'ph-shield-check',
    sections: [],
    optional: true
  },
  {
    key: ONBOARDING_STEP.REVIEW,
    icon: 'ph-clipboard-text',
    sections: []
  }
]

/**
 * Review is the only step that waits for the others: it shows a summary and submits,
 * and neither means anything while answers are still missing. Every other step is open
 * from the moment the email is verified, because the spec lets suppliers work through
 * steps 3–8 in whatever order suits them.
 */
export const GATED_STEPS = [ONBOARDING_STEP.REVIEW]

/** How long typing must pause before an autosave fires. */
export const AUTOSAVE_DELAY_MS = 1500

/**
 * Contract types the platform issues, mirroring ContractTypes in nozzle.supplier.
 *
 * One list, used for the admin filter tabs, the type dropdown and the row labels alike.
 * They were three hard-coded pairs before, and the label was a binary ternary that
 * reported every unknown type as "Purchase" — so adding a type to the backend silently
 * mislabelled it in the admin list.
 *
 * `labelKey` resolves under `contracts.types.*`.
 */
export const CONTRACT_TYPES = [
  { value: 'Membership', labelKey: 'contracts.types.membership' },
  { value: 'Purchase', labelKey: 'contracts.types.purchase' },
  { value: 'SupplierAgreement', labelKey: 'contracts.types.supplierAgreement' },
  { value: 'KvkkConsent', labelKey: 'contracts.types.kvkkConsent' },
  { value: 'AntiBriberySanctions', labelKey: 'contracts.types.antiBriberySanctions' },
  { value: 'ListingConsent', labelKey: 'contracts.types.listingConsent' }
]
